import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  Agreement,
  PaginatedResult,
  Ticket,
  TimeEntry,
  TimesheetTaskRow,
} from '@catalyst-psa/shared';
import { isTimesheetEditable } from '@catalyst-psa/shared';
import { useQueries, useQuery } from '@tanstack/react-query';

import { api, mutationError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { useDetailOverlay } from '../../components/layout/DetailOverlay.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { TextAreaField } from '../../components/ui/Field.js';
import { Icon } from '../../components/ui/Icon.js';
import { Modal } from '../../components/ui/Modal.js';
import { TabNav } from '../../components/ui/TabNav.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import { formatDate, formatDateTime } from '../../utils/date.js';
import { useProjects } from '../projects/api.js';
import { useTickets } from '../tickets/api.js';
import { ticketRefLabel } from '../tickets/reference.js';
import { TimeEntryFormModal, type WorkItemDefaults } from '../time-entries/TimeEntryFormModal.js';
import {
  useBillingClassOptions,
  useMemberOptions,
  useServiceCategoryOptions,
  useTimeEntries,
} from '../time-entries/api.js';
import { STATUS_LABELS, STATUS_TONES, SUBMISSION_TIMING_LABELS, SUBMISSION_TIMING_TONES } from './status.js';
import { CalendarWeekModal } from './CalendarWeekModal.js';
import { DueIndicator } from './DueIndicator.js';
import {
  useApproveTimesheet,
  useCopyPreviousWeek,
  useDeleteTimesheet,
  useLoadOpenTicketRows,
  useLoadPreviousWeekRows,
  useRejectTimesheet,
  useRemoveEmptyEntries,
  useReopenTimesheet,
  useSetTimesheetBonusDisqualification,
  useSetTimesheetTiming,
  useSubmitTimesheet,
  useTimesheet,
  useUpdateTimesheet,
} from './api.js';

type WorkItemType = 'project' | 'support' | 'charge_code';

const TYPE_LABELS: Record<WorkItemType, string> = {
  project: 'Project',
  support: 'Support',
  charge_code: 'Charge Code',
};

const TYPE_TONES: Record<WorkItemType, 'green' | 'amber' | 'blue'> = {
  project: 'blue',
  support: 'green',
  charge_code: 'amber',
};

/** Active summary filter applied to the grid + raw entry list (Part 2). */
type TimesheetFilter =
  | { kind: 'billable'; value: 'billable' | 'non-billable' }
  | { kind: 'customer'; value: string }
  | { kind: 'chargeCode'; value: string }
  | { kind: 'serviceCategory'; value: string }
  | { kind: 'billingClass'; value: string };

const FILTER_KIND_LABELS: Record<TimesheetFilter['kind'], string> = {
  billable: 'Billable',
  customer: 'Customer',
  chargeCode: 'Charge code',
  serviceCategory: 'Service category',
  billingClass: 'Billing class',
};

function describeFilter(filter: TimesheetFilter): string {
  if (filter.kind === 'billable') {
    return filter.value === 'billable' ? 'Billable hours' : 'Non-billable hours';
  }
  return `${FILTER_KIND_LABELS[filter.kind]}: ${filter.value}`;
}

/**
 * Classifies a work item for the timesheet grid. A charge-code entry is flagged by the
 * server (`isChargeCode`): either time on a designated charge-code project, or a ticket
 * entry carrying neither a project nor an agreement. A project entry has a project;
 * everything else is agreement-based support.
 */
function workItemTypeOf(entry: TimeEntry): WorkItemType {
  if (entry.isChargeCode ?? (entry.ticketId && !entry.projectId && !entry.agreementId))
    return 'charge_code';
  if (entry.projectId) return 'project';
  return 'support';
}

interface GridRow {
  key: string;
  type: WorkItemType;
  /** True for an unsaved "ghost" row (a loaded task with no entries yet). */
  ghost?: boolean;
  /** Set when the row's ticket is closed but still accepts time until this ISO instant. */
  graceUntil?: string | null;
  /** Customer the work item belongs to (resolved via ticket, project, or agreement). */
  customerName: string;
  /** Ticket number when the row is tied to a ticket, else null. */
  ticketNumber: number | null;
  /** Ticket reference (ClickUp custom ID) for support tickets, else null. */
  ticketReference: string | null;
  /** Ticket title when the row is tied to a ticket, else null. */
  ticketTitle: string | null;
  /** Agreement name (support) or project name (project) the work item rolls up to. */
  parentName: string;
  /** Ticket type name for support tickets, else null. */
  ticketTypeName: string | null;
  /** Stable label used only for sorting rows. */
  sortLabel: string;
  workItem: WorkItemDefaults;
}

function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatDayHeader(iso: string): { weekday: string; date: string } {
  const date = new Date(`${iso}T00:00:00.000Z`);
  return {
    weekday: date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
    date: date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', timeZone: 'UTC' }),
  };
}

function sumHours(entries: TimeEntry[]): number {
  return entries.reduce((total, entry) => total + Number(entry.hours), 0);
}

function formatHours(value: number): string {
  return value === 0 ? '' : value.toFixed(2);
}

/** Formats an ISO instant as a readable local date + time (matches the history feed). */
function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function rowKeyOf(entry: TimeEntry): string {
  return [
    entry.projectId ?? '',
    entry.agreementId ?? '',
    entry.phaseId ?? '',
    entry.ticketId ?? '',
  ].join('|');
}

function BreakdownList({
  title,
  items,
  onSelect,
  activeValue,
}: {
  title: string;
  items: Array<[string, number]>;
  /** When provided, each row becomes a clickable filter button. */
  onSelect?: (label: string) => void;
  /** The currently-active value for this breakdown (highlights the row). */
  activeValue?: string | null;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</p>
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-slate-400">—</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {items.map(([label, hours]) => {
            const isActive = activeValue === label;
            const content = (
              <>
                <span className="truncate text-left text-slate-700">{label}</span>
                <span className="shrink-0 font-medium tabular-nums text-slate-900">
                  {hours.toFixed(2)}
                </span>
              </>
            );
            return (
              <li key={label}>
                {onSelect ? (
                  <button
                    type="button"
                    onClick={() => onSelect(label)}
                    className={`flex w-full items-baseline justify-between gap-3 rounded px-1 text-sm hover:bg-brand-50 ${
                      isActive ? 'bg-brand-50 ring-1 ring-brand-300' : ''
                    }`}
                  >
                    {content}
                  </button>
                ) : (
                  <div className="flex items-baseline justify-between gap-3 text-sm">{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function TimesheetDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { inOverlay } = useDetailOverlay();
  const { can, isAdmin, isTimeApprover, canSeeMenu, principal } = useAuth();
  const canEdit = can('time', 'edit');
  // Admins and anyone with the Approvals menu item at view level or above may
  // view any member's timesheet; other members are scoped to their own.
  const canSeeAll = canSeeMenu('approvals');
  const currentMemberId = principal?.type === 'member' ? principal.id : '';

  const timesheetQuery = useTimesheet(id);
  const entriesQuery = useTimeEntries({ page: 1, pageSize: 200, timesheetId: id });
  const membersQuery = useMemberOptions();
  const projectsQuery = useProjects({ page: 1, pageSize: 200 });
  const ticketsQuery = useTickets({ page: 1, pageSize: 200 });
  const agreementsQuery = useQuery({
    queryKey: ['agreements', 'options'] as const,
    queryFn: () => api.get<PaginatedResult<Agreement>>('/agreements?page=1&pageSize=200'),
  });
  const serviceCategoriesQuery = useServiceCategoryOptions();
  const billingClassesQuery = useBillingClassOptions();
  const submitMutation = useSubmitTimesheet();
  const approveMutation = useApproveTimesheet();
  const rejectMutation = useRejectTimesheet();
  const copyMutation = useCopyPreviousWeek();
  const loadTicketsMutation = useLoadOpenTicketRows();
  const loadPreviousMutation = useLoadPreviousWeekRows();
  const removeEmptyMutation = useRemoveEmptyEntries();
  const updateMutation = useUpdateTimesheet();
  const disqualifyMutation = useSetTimesheetBonusDisqualification();
  const reopenMutation = useReopenTimesheet();
  const deleteMutation = useDeleteTimesheet();
  const timingMutation = useSetTimesheetTiming();

  const [formOpen, setFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [initialWorkDate, setInitialWorkDate] = useState<string | undefined>(undefined);
  const [initialWorkItem, setInitialWorkItem] = useState<WorkItemDefaults | undefined>(undefined);
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [disqualifyNoteDraft, setDisqualifyNoteDraft] = useState<string | null>(null);
  const [tab, setTab] = useState<'details' | 'history'>('details');
  const [filter, setFilter] = useState<TimesheetFilter | null>(null);
  const [submitInfo, setSubmitInfo] = useState<string | null>(null);
  /** Unsaved rows loaded on demand; cleared on page leave (never persisted). */
  const [ghostRows, setGhostRows] = useState<TimesheetTaskRow[]>([]);
  const [loadInfo, setLoadInfo] = useState<string | null>(null);
  const [copyWithTimeOpen, setCopyWithTimeOpen] = useState(false);
  const [unsubmitOpen, setUnsubmitOpen] = useState(false);
  const [unsubmitReason, setUnsubmitReason] = useState('');
  const [reopenOpen, setReopenOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const navigate = useNavigate();

  const timesheet = timesheetQuery.data;
  const entries = useMemo(() => entriesQuery.data?.data ?? [], [entriesQuery.data]);

  // The paginated tickets list may not include every ticket referenced by these
  // entries (e.g. older/closed tickets beyond the first page), so fetch each
  // referenced ticket by id to guarantee the number/title resolve.
  const referencedTicketIds = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of entries) {
      if (entry.ticketId) ids.add(entry.ticketId);
    }
    return [...ids];
  }, [entries]);
  const ticketDetailQueries = useQueries({
    queries: referencedTicketIds.map((ticketId) => ({
      queryKey: ['tickets', 'detail', ticketId] as const,
      queryFn: () => api.get<Ticket>(`/tickets/${ticketId}`),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const fetchedTickets = useMemo(
    () => ticketDetailQueries.map((q) => q.data).filter((t): t is Ticket => Boolean(t)),
    [ticketDetailQueries],
  );

  const memberName = useMemo(() => {
    if (!timesheet) return '';
    const member = (membersQuery.data ?? []).find((m) => m.id === timesheet.memberId);
    return member?.displayName ?? '';
  }, [timesheet, membersQuery.data]);

  const projectNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const project of projectsQuery.data?.data ?? []) {
      map.set(project.id, `#${project.number} — ${project.name}`);
    }
    return map;
  }, [projectsQuery.data]);
  const agreementNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const agreement of agreementsQuery.data?.data ?? []) {
      map.set(agreement.id, agreement.name);
    }
    return map;
  }, [agreementsQuery.data]);
  const ticketsById = useMemo(() => {
    const map = new Map<string, Ticket>();
    for (const ticket of ticketsQuery.data?.data ?? []) {
      map.set(ticket.id, ticket);
    }
    // Per-id fetches win over the list so referenced tickets always resolve.
    for (const ticket of fetchedTickets) {
      map.set(ticket.id, ticket);
    }
    return map;
  }, [ticketsQuery.data, fetchedTickets]);

  // Customer name lookups so each work-item row can show which customer it belongs to.
  const projectCustomers = useMemo(() => {
    const map = new Map<string, string>();
    for (const project of projectsQuery.data?.data ?? []) {
      map.set(project.id, project.customerName);
    }
    return map;
  }, [projectsQuery.data]);
  const agreementCustomers = useMemo(() => {
    const map = new Map<string, string>();
    for (const agreement of agreementsQuery.data?.data ?? []) {
      if (agreement.customerName) map.set(agreement.id, agreement.customerName);
    }
    return map;
  }, [agreementsQuery.data]);
  const serviceCategoryNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const sc of serviceCategoriesQuery.data ?? []) {
      map.set(sc.id, sc.name);
    }
    return map;
  }, [serviceCategoriesQuery.data]);
  const billingClassNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const bc of billingClassesQuery.data ?? []) {
      map.set(bc.id, bc.name);
    }
    return map;
  }, [billingClassesQuery.data]);

  // Resolves the customer name for an entry via its ticket first, then the project or
  // agreement it rolls up to, so a customer always shows for project and support rows.
  const customerOf = useCallback(
    (entry: TimeEntry): string => {
      if (entry.ticketId) {
        const ticket = ticketsById.get(entry.ticketId);
        if (ticket?.customerName) return ticket.customerName;
      }
      // The entry carries its own resolved customer (from the time-entries endpoint),
      // which is reliable even when the project/agreement is outside the capped lists.
      if (entry.customerName) return entry.customerName;
      if (entry.projectId) return projectCustomers.get(entry.projectId) ?? '';
      if (entry.agreementId) return agreementCustomers.get(entry.agreementId) ?? '';
      return '';
    },
    [ticketsById, projectCustomers, agreementCustomers],
  );

  // Charge-code label for an entry (its ticket), mirroring the breakdown grouping.
  const chargeCodeLabelOf = useCallback(
    (entry: TimeEntry): string => {
      const ticket = entry.ticketId ? ticketsById.get(entry.ticketId) : undefined;
      return ticket ? `#${ticket.number} — ${ticket.title}` : 'Charge code';
    },
    [ticketsById],
  );

  // Filters the grid + raw list when a summary number is clicked.
  const matchesFilter = useCallback(
    (entry: TimeEntry, active: TimesheetFilter): boolean => {
      switch (active.kind) {
        case 'billable':
          return active.value === 'billable' ? entry.billable : !entry.billable;
        case 'customer':
          return (customerOf(entry) || 'Unassigned') === active.value;
        case 'chargeCode':
          return (
            workItemTypeOf(entry) === 'charge_code' && chargeCodeLabelOf(entry) === active.value
          );
        case 'serviceCategory':
          return (serviceCategoryNames.get(entry.serviceCategoryId) ?? '—') === active.value;
        case 'billingClass':
          return (billingClassNames.get(entry.billingClassId) ?? '—') === active.value;
        default:
          return true;
      }
    },
    [customerOf, chargeCodeLabelOf, serviceCategoryNames, billingClassNames],
  );

  const filteredEntries = useMemo(
    () => (filter ? entries.filter((e) => matchesFilter(e, filter)) : entries),
    [entries, filter, matchesFilter],
  );

  // Raw read-only list of individual entries (Part 1), ordered by work date.
  const rawListEntries = useMemo(
    () =>
      [...filteredEntries].sort(
        (a, b) =>
          a.workDate.localeCompare(b.workDate) ||
          (a.workLabel ?? '').localeCompare(b.workLabel ?? ''),
      ),
    [filteredEntries],
  );

  const days = useMemo(
    () =>
      timesheet ? Array.from({ length: 7 }, (_, i) => addDays(timesheet.weekStartDate, i)) : [],
    [timesheet],
  );

  // Group entries into grid rows (one per work item) and per-day cells.
  const { rows, cells } = useMemo(() => {
    const rowMap = new Map<string, GridRow>();
    const cellMap = new Map<string, TimeEntry[]>();
    for (const entry of filteredEntries) {
      const key = rowKeyOf(entry);
      if (!rowMap.has(key)) {
        const ticket = entry.ticketId ? ticketsById.get(entry.ticketId) : undefined;
        const type = workItemTypeOf(entry);
        // Charge-code rows are identified by their ticket (the charge code), so the
        // container project name is redundant; suppress it.
        const parentName =
          type === 'charge_code'
            ? ''
            : entry.projectId
              ? (entry.workLabel ?? projectNames.get(entry.projectId) ?? 'Project')
              : entry.agreementId
                ? (entry.workLabel ?? agreementNames.get(entry.agreementId) ?? 'Agreement')
                : '';
        const customerName = customerOf(entry);
        rowMap.set(key, {
          key,
          type,
          customerName,
          ticketNumber: ticket?.number ?? null,
          ticketReference: ticket?.referenceId ?? null,
          ticketTitle: ticket?.title ?? null,
          parentName,
          ticketTypeName: ticket?.ticketTypeName ?? null,
          sortLabel: [
            customerName,
            ticket ? String(ticket.number).padStart(8, '0') : parentName,
          ].join('|'),
          workItem: {
            projectId: entry.projectId,
            agreementId: entry.agreementId,
            phaseId: entry.phaseId,
            ticketId: entry.ticketId,
            serviceCategoryId: entry.serviceCategoryId,
            billingClassId: entry.billingClassId,
          },
        });
      }
      const cellKey = `${key}__${entry.workDate}`;
      const list = cellMap.get(cellKey) ?? [];
      list.push(entry);
      cellMap.set(cellKey, list);
    }
    const sortedRows = [...rowMap.values()].sort((a, b) => a.sortLabel.localeCompare(b.sortLabel));
    return { rows: sortedRows, cells: cellMap };
  }, [filteredEntries, ticketsById, projectNames, agreementNames, customerOf]);

  // Unsaved "ghost" rows from “Copy last week's tasks” / “Load my open tickets”. Purely
  // client-side: nothing is written until hours are logged in a cell, at which point the
  // real entry row appears and supersedes the ghost (deduped by work-item key). Hidden
  // while a summary filter is active, and lost on page leave — press the button again.
  const displayRows = useMemo(() => {
    if (filter || ghostRows.length === 0) return rows;
    const existingKeys = new Set(rows.map((r) => r.key));
    const ghosts: GridRow[] = [];
    for (const t of ghostRows) {
      const key = [t.projectId ?? '', t.agreementId ?? '', t.phaseId ?? '', t.ticketId ?? ''].join(
        '|',
      );
      if (existingKeys.has(key)) continue;
      existingKeys.add(key);
      const type: WorkItemType =
        !t.projectId && !t.agreementId ? 'charge_code' : t.projectId ? 'project' : 'support';
      ghosts.push({
        key,
        type,
        ghost: true,
        graceUntil: t.timeEntryGraceUntil ?? null,
        customerName: t.customerName ?? '',
        ticketNumber: t.ticketNumber,
        ticketReference: t.ticketReference,
        ticketTitle: t.ticketTitle,
        parentName: type === 'charge_code' ? '' : (t.parentName ?? ''),
        ticketTypeName: t.ticketTypeName,
        sortLabel: [t.customerName ?? '', String(t.ticketNumber ?? 0).padStart(8, '0')].join('|'),
        workItem: {
          projectId: t.projectId,
          agreementId: t.agreementId,
          phaseId: t.phaseId,
          ticketId: t.ticketId,
          serviceCategoryId: t.serviceCategoryId,
          billingClassId: t.billingClassId,
        },
      });
    }
    if (ghosts.length === 0) return rows;
    return [...rows, ...ghosts].sort((a, b) => a.sortLabel.localeCompare(b.sortLabel));
  }, [rows, ghostRows, filter]);

  /** Merges freshly loaded rows into the ghost set, keeping any already on screen. */
  const addGhostRows = useCallback((incoming: TimesheetTaskRow[]) => {
    setGhostRows((current) => {
      const keyOf = (t: TimesheetTaskRow): string =>
        [t.projectId ?? '', t.agreementId ?? '', t.phaseId ?? '', t.ticketId ?? ''].join('|');
      const seen = new Set(current.map(keyOf));
      const added = incoming.filter((t) => !seen.has(keyOf(t)));
      return added.length === 0 ? current : [...current, ...added];
    });
  }, []);

  /** Applies a summary filter, or clears it when the same one is clicked again. */
  const toggleFilter = useCallback((next: TimesheetFilter) => {
    setFilter((current) =>
      current && current.kind === next.kind && current.value === next.value ? null : next,
    );
  }, []);

  const editable =
    Boolean(timesheet) &&
    canEdit &&
    isTimesheetEditable(timesheet!.status) &&
    (canSeeAll || timesheet!.memberId === currentMemberId);

  // Every summary number reflects the ACTIVE FILTER, so clicking a tile recalculates the
  // whole page. With no filter these equal the timesheet's stored totals (the entry query
  // pulls the full week, so the sums are complete).
  const totalHours = useMemo(() => sumHours(filteredEntries), [filteredEntries]);
  const billableHours = useMemo(
    () => sumHours(filteredEntries.filter((e) => e.billable)),
    [filteredEntries],
  );
  const nonBillableHours = totalHours - billableHours;
  const percentBillable = totalHours > 0 ? (billableHours / totalHours) * 100 : 0;

  const dayTotals = useMemo(
    () => days.map((day) => sumHours(filteredEntries.filter((e) => e.workDate === day))),
    [days, filteredEntries],
  );

  // Header hour breakdowns: per customer, per charge code, per service category, and per
  // billing class. Zero-hour rows are skipped so the lists only reflect logged work.
  const breakdowns = useMemo(() => {
    const accumulate = (map: Map<string, number>, label: string, hours: number): void => {
      map.set(label, (map.get(label) ?? 0) + hours);
    };
    const byCustomer = new Map<string, number>();
    const byChargeCode = new Map<string, number>();
    const byServiceCategory = new Map<string, number>();
    const byBillingClass = new Map<string, number>();
    for (const entry of filteredEntries) {
      const hours = Number(entry.hours);
      if (hours === 0) continue;
      accumulate(byCustomer, customerOf(entry) || 'Unassigned', hours);
      if (workItemTypeOf(entry) === 'charge_code') {
        const ticket = entry.ticketId ? ticketsById.get(entry.ticketId) : undefined;
        const label = ticket ? `#${ticket.number} — ${ticket.title}` : 'Charge code';
        accumulate(byChargeCode, label, hours);
      }
      accumulate(
        byServiceCategory,
        serviceCategoryNames.get(entry.serviceCategoryId) ?? '—',
        hours,
      );
      accumulate(byBillingClass, billingClassNames.get(entry.billingClassId) ?? '—', hours);
    }
    const toSorted = (map: Map<string, number>): Array<[string, number]> =>
      [...map.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return {
      byCustomer: toSorted(byCustomer),
      byChargeCode: toSorted(byChargeCode),
      byServiceCategory: toSorted(byServiceCategory),
      byBillingClass: toSorted(byBillingClass),
    };
  }, [filteredEntries, customerOf, ticketsById, serviceCategoryNames, billingClassNames]);

  const openCell = (row: GridRow, day: string): void => {
    if (!editable) return;
    const cellEntries = cells.get(`${row.key}__${day}`) ?? [];
    const single = cellEntries.length === 1 ? cellEntries[0] : undefined;
    if (single) {
      setEditingEntry(single);
      setInitialWorkDate(undefined);
      setInitialWorkItem(undefined);
    } else {
      setEditingEntry(null);
      setInitialWorkDate(day);
      setInitialWorkItem(row.workItem);
    }
    setFormOpen(true);
  };

  const openAddLine = (): void => {
    setEditingEntry(null);
    setInitialWorkDate(timesheet?.weekStartDate);
    setInitialWorkItem(undefined);
    setFormOpen(true);
  };

  const canSubmit = editable && timesheet!.status !== 'submitted';
  // Time approvers (and admins) may approve / un-submit a submitted timesheet from here.
  const canApprove = !!timesheet && timesheet.status === 'submitted' && (isAdmin || isTimeApprover);
  // Admins may reopen an approved timesheet back to draft to correct + resubmit it.
  const canReopen = !!timesheet && timesheet.status === 'approved' && isAdmin;
  // Admins may permanently delete a draft timesheet (and its never-submitted entries).
  const canDelete = !!timesheet && timesheet.status === 'draft' && isAdmin;
  // Time approvers (and admins) may disqualify this week from the monthly bonus.
  const canManageBonus = isAdmin || isTimeApprover;

  if (timesheetQuery.isLoading || !timesheet) {
    return (
      <div>
        {inOverlay ? null : (
          <Link
            to="/timesheets"
            className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            ← Back to timesheets
          </Link>
        )}
        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <DataState
            isLoading={timesheetQuery.isLoading}
            isError={timesheetQuery.isError}
            error={timesheetQuery.error}
            onRetry={() => void timesheetQuery.refetch()}
            isEmpty={!timesheetQuery.isLoading && !timesheet}
            emptyTitle="Timesheet not found"
            emptyDescription="This timesheet may have been removed."
          >
            <div />
          </DataState>
        </div>
      </div>
    );
  }

  return (
    <div>
      {inOverlay ? null : (
        <Link
          to="/timesheets"
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          ← Back to timesheets
        </Link>
      )}

      <PageHeader
        title={`Week of ${formatDate(timesheet.weekStartDate)}`}
        description={memberName ? `Timesheet for ${memberName}` : 'Weekly timesheet'}
        actions={
          <div className="flex items-center gap-2">
            <Badge tone={STATUS_TONES[timesheet.status]}>{STATUS_LABELS[timesheet.status]}</Badge>
            {currentMemberId && timesheet.memberId === currentMemberId ? (
              <Button variant="secondary" onClick={() => setCalendarOpen(true)}>
                <Icon name="clock" className="h-4 w-4" />
                My calendar
              </Button>
            ) : null}
            {editable ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() =>
                    loadPreviousMutation.mutate(timesheet.id, {
                      onSuccess: (result) => {
                        addGhostRows(result.rows);
                        setLoadInfo(
                          `Loaded ${result.rows.length} task${
                            result.rows.length === 1 ? '' : 's'
                          } from last week. Rows are not saved — an entry is only created when you log hours.`,
                        );
                      },
                    })
                  }
                  isLoading={loadPreviousMutation.isPending}
                  disabled={loadPreviousMutation.isPending}
                >
                  Copy last week&apos;s tasks
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setCopyWithTimeOpen(true)}
                  isLoading={
                    copyMutation.isPending && copyMutation.variables?.input.includeTime === true
                  }
                  disabled={copyMutation.isPending}
                >
                  Copy last week&apos;s tasks and time
                </Button>
                <Button
                  variant="secondary"
                  onClick={() =>
                    loadTicketsMutation.mutate(timesheet.id, {
                      onSuccess: (result) => {
                        addGhostRows(result.rows);
                        setLoadInfo(
                          `Loaded ${result.rows.length} open ticket${
                            result.rows.length === 1 ? '' : 's'
                          }${
                            result.skipped > 0
                              ? ` (${result.skipped} skipped — missing billing defaults)`
                              : ''
                          }. Rows are not saved — an entry is only created when you log hours.`,
                        );
                      },
                    })
                  }
                  isLoading={loadTicketsMutation.isPending}
                  disabled={loadTicketsMutation.isPending}
                >
                  Load my open tickets
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => removeEmptyMutation.mutate(timesheet.id)}
                  isLoading={removeEmptyMutation.isPending}
                  disabled={removeEmptyMutation.isPending}
                >
                  Remove empty tickets
                </Button>
                <Button variant="secondary" onClick={openAddLine}>
                  <Icon name="time" className="h-4 w-4" />
                  Add line
                </Button>
              </>
            ) : null}
            {canSubmit ? (
              <Button
                onClick={() =>
                  submitMutation.mutate(timesheet.id, {
                    onSuccess: (updated) => {
                      const next = [...updated.approvalSteps]
                        .filter((s) => s.status === 'pending')
                        .sort((a, b) => a.sortOrder - b.sortOrder)[0];
                      setSubmitInfo(
                        next
                          ? `Submitted. Approval request emailed to ${next.approverName}.`
                          : 'Submitted. No approver is configured for this member, so no approval ' +
                              'email was sent.',
                      );
                    },
                  })
                }
                isLoading={submitMutation.isPending}
              >
                Submit
              </Button>
            ) : null}
            {canApprove ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setUnsubmitReason('');
                    rejectMutation.reset();
                    setUnsubmitOpen(true);
                  }}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                >
                  Un-Submit
                </Button>
                <Button
                  onClick={() => approveMutation.mutate(timesheet.id)}
                  isLoading={approveMutation.isPending}
                  disabled={rejectMutation.isPending}
                >
                  Approve
                </Button>
              </>
            ) : null}
            {canReopen ? (
              <Button
                variant="secondary"
                onClick={() => {
                  reopenMutation.reset();
                  setReopenOpen(true);
                }}
                disabled={reopenMutation.isPending}
              >
                Reopen to Draft
              </Button>
            ) : null}
          </div>
        }
      />

      <TabNav
        tabs={[
          { id: 'details', label: 'Details' },
          { id: 'history', label: 'History' },
        ]}
        activeId={tab}
        onChange={(id) => setTab(id as 'details' | 'history')}
      />

      {tab === 'history' ? <HistoryPanel entityType="Timesheet" entityId={timesheet.id} /> : null}

      <div className={tab === 'details' ? '' : 'hidden'}>
        {submitMutation.isError ? (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span>{mutationError(submitMutation.error, 'Failed to submit the timesheet.')}</span>
            <button
              type="button"
              onClick={() => submitMutation.reset()}
              className="text-rose-600 hover:text-rose-800"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ) : null}

        {submitInfo ? (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <span>{submitInfo}</span>
            <button
              type="button"
              onClick={() => setSubmitInfo(null)}
              className="text-emerald-600 hover:text-emerald-800"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ) : null}

        {loadInfo ? (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-lg bg-sky-50 px-4 py-3 text-sm text-sky-700">
            <span>{loadInfo}</span>
            <button
              type="button"
              onClick={() => setLoadInfo(null)}
              className="text-sky-600 hover:text-sky-800"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ) : null}

        {timesheet.status === 'rejected' && timesheet.rejectedReason ? (
          <div className="mb-4 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Un-submitted: {timesheet.rejectedReason}
          </div>
        ) : null}

        {timesheet.submissionTiming || timesheet.dueStatus ? (
          <div className="mb-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Due</span>
            <DueIndicator timesheet={timesheet} />
          </div>
        ) : null}

        {isAdmin ? (
          <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Submission timing (admin)
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                variant={timesheet.submissionTiming === 'on_time' ? 'primary' : 'secondary'}
                onClick={() =>
                  timingMutation.mutate({ id: timesheet.id, input: { timing: 'on_time' } })
                }
                isLoading={
                  timingMutation.isPending &&
                  timingMutation.variables?.input.timing === 'on_time'
                }
                disabled={timingMutation.isPending}
              >
                Mark on-time
              </Button>
              <Button
                variant={timesheet.submissionTiming === 'past_due' ? 'primary' : 'secondary'}
                onClick={() =>
                  timingMutation.mutate({ id: timesheet.id, input: { timing: 'past_due' } })
                }
                isLoading={
                  timingMutation.isPending &&
                  timingMutation.variables?.input.timing === 'past_due'
                }
                disabled={timingMutation.isPending}
              >
                Mark past due
              </Button>
              {timesheet.submissionTimingOverride ? (
                <Button
                  variant="ghost"
                  onClick={() =>
                    timingMutation.mutate({ id: timesheet.id, input: { timing: null } })
                  }
                  isLoading={
                    timingMutation.isPending && timingMutation.variables?.input.timing === null
                  }
                  disabled={timingMutation.isPending}
                >
                  Clear override
                </Button>
              ) : null}
              <span className="ml-1 flex items-center gap-2">
                {timesheet.submissionTiming ? (
                  <Badge tone={SUBMISSION_TIMING_TONES[timesheet.submissionTiming]}>
                    {SUBMISSION_TIMING_LABELS[timesheet.submissionTiming]}
                  </Badge>
                ) : (
                  <span className="text-sm text-slate-500">Not set</span>
                )}
                {timesheet.submissionTimingOverride ? (
                  <span className="text-xs text-slate-400">(manual override)</span>
                ) : null}
              </span>
            </div>
          </div>
        ) : null}

        {canManageBonus ? (
          <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Bonus eligibility
            </p>
            <div className="mt-2 space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={timesheet.bonusDisqualified}
                  onChange={(e) =>
                    disqualifyMutation.mutate({
                      id: timesheet.id,
                      disqualified: e.target.checked,
                      note: e.target.checked
                        ? (
                            disqualifyNoteDraft ??
                            timesheet.bonusDisqualifiedNote ??
                            ''
                          ).trim() || null
                        : null,
                    })
                  }
                  className="h-4 w-4 rounded border-slate-300"
                />
                Disqualify this week from the monthly bonus
              </label>
              {timesheet.bonusDisqualified ? (
                <input
                  type="text"
                  value={disqualifyNoteDraft ?? timesheet.bonusDisqualifiedNote ?? ''}
                  onChange={(e) => setDisqualifyNoteDraft(e.target.value)}
                  onBlur={() => {
                    if (disqualifyNoteDraft === null) return;
                    const next = disqualifyNoteDraft.trim() || null;
                    if (next !== (timesheet.bonusDisqualifiedNote ?? null)) {
                      disqualifyMutation.mutate({
                        id: timesheet.id,
                        disqualified: true,
                        note: next,
                      });
                    }
                  }}
                  placeholder="Reason (optional)"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              ) : null}
            </div>
          </div>
        ) : null}

        {timesheet.approvalSteps.length > 0 ? (
          <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Approval chain
            </p>
            <ol className="mt-2 flex flex-wrap items-start gap-x-2 gap-y-2">
              {timesheet.submittedAt ? (
                <li className="flex flex-col">
                  <span className="flex items-center gap-2">
                    <span className="text-sm text-slate-900">Submitted</span>
                    <Badge tone="blue">Done</Badge>
                  </span>
                  <span className="mt-0.5 text-xs tabular-nums text-slate-400">
                    {formatWhen(timesheet.submittedAt)}
                  </span>
                </li>
              ) : null}
              {timesheet.approvalSteps.map((step, index) => {
                const isCurrent =
                  timesheet.status === 'submitted' &&
                  step.status === 'pending' &&
                  timesheet.approvalSteps
                    .filter((s) => s.status === 'pending')
                    .sort((a, b) => a.sortOrder - b.sortOrder)[0]?.id === step.id;
                const tone =
                  step.status === 'approved'
                    ? 'green'
                    : step.status === 'rejected'
                      ? 'red'
                      : isCurrent
                        ? 'blue'
                        : 'gray';
                return (
                  <li key={step.id} className="flex items-start gap-2">
                    <span className="text-slate-300">→</span>
                    <span className="flex flex-col">
                      <span className="flex items-center gap-2">
                        <span className="text-xs tabular-nums text-slate-400">{index + 1}.</span>
                        <span className="text-sm text-slate-900">{step.approverName}</span>
                        <Badge tone={tone}>
                          {step.status === 'approved'
                            ? 'Approved'
                            : step.status === 'rejected'
                              ? 'Un-submitted'
                              : isCurrent
                                ? 'Awaiting'
                                : 'Pending'}
                        </Badge>
                      </span>
                      <span className="mt-0.5 text-xs tabular-nums text-slate-400">
                        {step.decidedAt
                          ? formatWhen(step.decidedAt)
                          : isCurrent
                            ? 'awaiting approval'
                            : '\u00a0'}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        ) : null}

        <div className="mb-4 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Total hours
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
              {totalHours.toFixed(2)}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Billable / Non-billable
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
              <button
                type="button"
                onClick={() => toggleFilter({ kind: 'billable', value: 'billable' })}
                className={`rounded px-1 hover:bg-brand-50 ${
                  filter?.kind === 'billable' && filter.value === 'billable'
                    ? 'bg-brand-50 ring-1 ring-brand-300'
                    : ''
                }`}
              >
                {billableHours.toFixed(2)}
              </button>
              <span className="text-base font-normal text-slate-400"> / </span>
              <button
                type="button"
                onClick={() => toggleFilter({ kind: 'billable', value: 'non-billable' })}
                className={`rounded px-1 text-base font-normal text-slate-400 hover:bg-brand-50 ${
                  filter?.kind === 'billable' && filter.value === 'non-billable'
                    ? 'bg-brand-50 ring-1 ring-brand-300'
                    : ''
                }`}
              >
                {nonBillableHours.toFixed(2)}
              </button>
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Percent billable
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
              {percentBillable.toFixed(0)}%
            </p>
          </div>
        </div>

        <div className="mb-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <BreakdownList
            title="Hours by customer"
            items={breakdowns.byCustomer}
            onSelect={(label) => toggleFilter({ kind: 'customer', value: label })}
            activeValue={filter?.kind === 'customer' ? filter.value : null}
          />
          <BreakdownList
            title="Hours by charge code"
            items={breakdowns.byChargeCode}
            onSelect={(label) => toggleFilter({ kind: 'chargeCode', value: label })}
            activeValue={filter?.kind === 'chargeCode' ? filter.value : null}
          />
          <BreakdownList
            title="Hours by service category"
            items={breakdowns.byServiceCategory}
            onSelect={(label) => toggleFilter({ kind: 'serviceCategory', value: label })}
            activeValue={filter?.kind === 'serviceCategory' ? filter.value : null}
          />
          <BreakdownList
            title="Hours by billing class"
            items={breakdowns.byBillingClass}
            onSelect={(label) => toggleFilter({ kind: 'billingClass', value: label })}
            activeValue={filter?.kind === 'billingClass' ? filter.value : null}
          />
        </div>

        {filter ? (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-brand-200 bg-brand-50 px-4 py-2 text-sm text-brand-800">
            <span>
              Filtered by <span className="font-semibold">{describeFilter(filter)}</span> ·{' '}
              {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}
            </span>
            <button
              type="button"
              onClick={() => setFilter(null)}
              className="rounded-md border border-brand-300 bg-white px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
            >
              Clear filter
            </button>
          </div>
        ) : null}

        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <label
            htmlFor="timesheet-notes"
            className="text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            Notes
          </label>
          {editable ? (
            <textarea
              id="timesheet-notes"
              rows={3}
              value={notesDraft ?? timesheet.notes ?? ''}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={() => {
                if (notesDraft !== null && notesDraft !== (timesheet.notes ?? '')) {
                  updateMutation.mutate({
                    id: timesheet.id,
                    input: { notes: notesDraft.trim() ? notesDraft.trim() : null },
                  });
                }
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Add notes for this week…"
            />
          ) : (
            <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
              {timesheet.notes ?? '—'}
            </p>
          )}
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Work item</th>
                {days.map((day) => {
                  const { weekday, date } = formatDayHeader(day);
                  return (
                    <th key={day} className="px-2 py-3 text-center font-medium">
                      <div>{weekday}</div>
                      <div className="font-normal text-slate-400">{date}</div>
                    </th>
                  );
                })}
                <th className="px-3 py-3 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <DataState
                colSpan={days.length + 2}
                isLoading={entriesQuery.isLoading}
                isError={entriesQuery.isError}
                error={entriesQuery.error}
                onRetry={() => void entriesQuery.refetch()}
                isEmpty={displayRows.length === 0}
                emptyTitle="No time logged"
                emptyDescription={
                  editable
                    ? 'Use “Add line” to log time for this week.'
                    : 'This timesheet has no time entries.'
                }
                emptyAction={editable ? <Button onClick={openAddLine}>Add line</Button> : undefined}
              >
                {displayRows.map((row) => {
                  const rowTotal = days.reduce(
                    (sum, day) => sum + sumHours(cells.get(`${row.key}__${day}`) ?? []),
                    0,
                  );
                  return (
                    <tr
                      key={row.key}
                      className={`align-middle ${row.ghost ? 'bg-slate-50/60' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          {row.customerName ? (
                            <span className="font-medium text-slate-900">{row.customerName}</span>
                          ) : null}
                          <Badge tone={TYPE_TONES[row.type]}>{TYPE_LABELS[row.type]}</Badge>
                          {row.ghost ? <Badge tone="gray">Not saved</Badge> : null}
                          {row.graceUntil ? (
                            <Badge tone="amber">{`Closed — time until ${formatDateTime(row.graceUntil)}`}</Badge>
                          ) : null}
                          {row.workItem.ticketId && row.ticketNumber !== null ? (
                            <a
                              href={`/tickets?open=${row.workItem.ticketId}`}
                              title="Open ticket"
                              className="text-brand-600 hover:text-brand-700 hover:underline"
                            >
                              <span className="tabular-nums">
                                {ticketRefLabel(row.ticketReference, row.ticketNumber)}
                              </span>
                              {row.ticketTitle ? <span> {row.ticketTitle}</span> : null}
                            </a>
                          ) : (
                            <>
                              {row.ticketNumber !== null ? (
                                <span className="tabular-nums text-slate-500">
                                  {ticketRefLabel(row.ticketReference, row.ticketNumber)}
                                </span>
                              ) : null}
                              {row.ticketTitle ? (
                                <span className="text-slate-900">{row.ticketTitle}</span>
                              ) : null}
                            </>
                          )}
                        </div>
                        {row.parentName || row.ticketTypeName ? (
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                            {row.parentName ? <span>{row.parentName}</span> : null}
                            {row.parentName && row.ticketTypeName ? <span>·</span> : null}
                            {row.ticketTypeName ? <span>{row.ticketTypeName}</span> : null}
                          </div>
                        ) : null}
                      </td>
                      {days.map((day) => {
                        const cellEntries = cells.get(`${row.key}__${day}`) ?? [];
                        const value = formatHours(sumHours(cellEntries));
                        return (
                          <td key={day} className="px-1 py-1 text-center">
                            {editable ? (
                              <button
                                type="button"
                                onClick={() => openCell(row, day)}
                                className="h-9 w-full rounded-lg border border-transparent text-sm tabular-nums text-slate-900 hover:border-slate-300 hover:bg-slate-50"
                              >
                                {value || <span className="text-slate-300">+</span>}
                              </button>
                            ) : (
                              <span className="block py-2 tabular-nums text-slate-700">
                                {value}
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-3 text-right font-medium tabular-nums text-slate-900">
                        {row.ghost ? '' : rowTotal.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </DataState>
            </tbody>
            {displayRows.length > 0 ? (
              <tfoot className="border-t border-slate-200">
                <tr>
                  <td className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-slate-500">
                    Totals
                  </td>
                  {dayTotals.map((total, index) => (
                    <td
                      key={days[index]}
                      className="px-2 py-3 text-center font-medium tabular-nums text-slate-900"
                    >
                      {total === 0 ? '' : total.toFixed(2)}
                    </td>
                  ))}
                  <td className="px-3 py-3 text-right font-semibold tabular-nums text-slate-900">
                    {totalHours.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-700">
              Time entries{' '}
              <span className="font-normal text-slate-400">({filteredEntries.length})</span>
            </h2>
            {filter ? (
              <span className="text-xs text-brand-700">Filtered: {describeFilter(filter)}</span>
            ) : null}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Ticket</th>
                  <th className="px-4 py-3 font-medium">Work item</th>
                  <th className="px-4 py-3 font-medium">Service category</th>
                  <th className="px-4 py-3 font-medium">Billing class</th>
                  <th className="px-4 py-3 text-right font-medium">Hours</th>
                  <th className="px-4 py-3 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rawListEntries.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-slate-400">
                      {filter ? 'No entries match this filter.' : 'No time entries.'}
                    </td>
                  </tr>
                ) : (
                  rawListEntries.map((entry) => {
                    const ticket = entry.ticketId ? ticketsById.get(entry.ticketId) : undefined;
                    return (
                      <tr key={entry.id}>
                        <td className="px-4 py-3 text-slate-600">{formatDate(entry.workDate)}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {entry.customerId ? (
                            <a
                              href={`/customers?returnCustomerId=${entry.customerId}&returnTab=details`}
                              className="text-brand-600 hover:text-brand-700 hover:underline"
                            >
                              {customerOf(entry) || 'Customer'}
                            </a>
                          ) : (
                            customerOf(entry) || '—'
                          )}
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {entry.ticketId ? (
                            <a
                              href={`/tickets?open=${entry.ticketId}`}
                              className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                            >
                              {ticket ? ticketRefLabel(ticket.referenceId, ticket.number) : '—'}
                            </a>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-900">
                          {entry.projectId || entry.agreementId ? (
                            <a
                              href={
                                entry.projectId
                                  ? `/projects/${entry.projectId}`
                                  : `/agreements/${entry.agreementId}`
                              }
                              className="text-brand-600 hover:text-brand-700 hover:underline"
                            >
                              {entry.workLabel ?? (entry.projectId ? 'Project' : 'Agreement')}
                            </a>
                          ) : (
                            (entry.workLabel ?? '—')
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {entry.serviceCategoryName ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {entry.billingClassName ?? '—'}
                          {entry.billable ? (
                            ''
                          ) : (
                            <span className="text-slate-400"> (non-billable)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                          {entry.hours}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{entry.notes ?? ''}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {canDelete ? (
          <div className="mt-6 flex justify-start">
            <Button
              variant="danger"
              onClick={() => {
                deleteMutation.reset();
                setDeleteOpen(true);
              }}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        ) : null}

        <TimeEntryFormModal
          isOpen={formOpen}
          entry={editingEntry}
          initialMemberId={timesheet.memberId}
          initialWorkDate={initialWorkDate}
          initialWorkItem={initialWorkItem}
          onClose={() => setFormOpen(false)}
        />

        <CalendarWeekModal
          isOpen={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          weekStartDate={timesheet.weekStartDate}
        />

        <Modal
          isOpen={copyWithTimeOpen}
          onClose={() => setCopyWithTimeOpen(false)}
          title="Copy last week's tasks and time"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setCopyWithTimeOpen(false)}
                disabled={copyMutation.isPending}
              >
                GO BACK
              </Button>
              <Button
                variant="danger"
                onClick={() =>
                  copyMutation.mutate(
                    { id: timesheet.id, input: { includeTime: true } },
                    { onSuccess: () => setCopyWithTimeOpen(false) },
                  )
                }
                isLoading={copyMutation.isPending}
              >
                DO IT ANYWAY
              </Button>
            </>
          }
        >
          <div className="rounded-lg border-2 border-rose-300 bg-rose-50 px-5 py-6 text-base font-semibold leading-relaxed text-rose-700">
            WARNING - this will copy all your tasks and TIME ENTRIES exactly as they are from last
            week to this week. You are not allowed to remove any time entires so if there is a least
            1 (one) you don&apos;t want, don&apos;t use this button.
          </div>
        </Modal>

        <Modal
          isOpen={unsubmitOpen}
          onClose={() => setUnsubmitOpen(false)}
          title="Un-Submit timesheet"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setUnsubmitOpen(false)}
                disabled={rejectMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() =>
                  rejectMutation.mutate(
                    { id: timesheet.id, input: { reason: unsubmitReason.trim() } },
                    { onSuccess: () => setUnsubmitOpen(false) },
                  )
                }
                isLoading={rejectMutation.isPending}
                disabled={!unsubmitReason.trim()}
              >
                Un-Submit
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-600">
              Provide a reason for un-submitting this timesheet. The member can revise and resubmit.
            </p>
            <TextAreaField
              label="Reason"
              required
              rows={3}
              value={unsubmitReason}
              maxLength={2000}
              onChange={(e) => setUnsubmitReason(e.target.value)}
              error={rejectMutation.isError ? 'Failed to un-submit. Try again.' : undefined}
            />
          </div>
        </Modal>

        <Modal
          isOpen={reopenOpen}
          onClose={() => setReopenOpen(false)}
          title="Reopen approved timesheet"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setReopenOpen(false)}
                disabled={reopenMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() =>
                  reopenMutation.mutate(timesheet.id, {
                    onSuccess: () => setReopenOpen(false),
                  })
                }
                isLoading={reopenMutation.isPending}
              >
                Reopen to Draft
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3 text-sm text-slate-600">
            <p>
              This moves the timesheet back to <strong>Draft</strong> so its entries can be edited
              and it can be resubmitted for approval. The approval chain is cleared.
            </p>
            <p>
              Timesheets whose time has already been placed on an invoice cannot be reopened.
            </p>
            {reopenMutation.isError ? (
              <p className="text-rose-600">
                Failed to reopen. It may already be invoiced or no longer approved.
              </p>
            ) : null}
          </div>
        </Modal>

        <Modal
          isOpen={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          title="Delete draft timesheet"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setDeleteOpen(false)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() =>
                  deleteMutation.mutate(timesheet.id, {
                    onSuccess: () => {
                      setDeleteOpen(false);
                      navigate('/timesheets');
                    },
                  })
                }
                isLoading={deleteMutation.isPending}
              >
                Delete timesheet
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3 text-sm text-slate-600">
            <p>
              This permanently deletes the draft timesheet{' '}
              <strong>and its {timesheet.entryCount} time entr{timesheet.entryCount === 1 ? 'y' : 'ies'}</strong>
              . This cannot be undone.
            </p>
            <p>Timesheets whose time is already on an invoice cannot be deleted.</p>
            {deleteMutation.isError ? (
              <p className="text-rose-600">
                Failed to delete. It may no longer be a draft, or its time is invoiced.
              </p>
            ) : null}
          </div>
        </Modal>
      </div>
    </div>
  );
}
